"use client";

import { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";

/**
 * Tooltip elegante con animación suave y posicionamiento inteligente.
 * Renderiza en un portal para evitar problemas de z-index y overflow.
 *
 * @param {React.ReactNode} children - Elemento que activa el tooltip.
 * @param {string} content - Texto del tooltip.
 * @param {"top"|"bottom"|"left"|"right"} position - Posición preferida del tooltip.
 * @param {boolean} [disabled] - Si es true, el tooltip no se muestra.
 */
export function Tooltip({ children, content, position = "bottom", disabled = false }) {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const showTimeout = useRef(null);

  const GAP = 10; // px entre el botón y el tooltip

  const compute = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();

    // Valores temporales para calcular el tamaño del tooltip
    const tooltipEl = tooltipRef.current;
    const tw = tooltipEl ? tooltipEl.offsetWidth : 120;
    const th = tooltipEl ? tooltipEl.offsetHeight : 32;

    let top = 0;
    let left = 0;

    switch (position) {
      case "top":
        top = rect.top - th - GAP + window.scrollY;
        left = rect.left + rect.width / 2 - tw / 2 + window.scrollX;
        break;
      case "bottom":
        top = rect.bottom + GAP + window.scrollY;
        left = rect.left + rect.width / 2 - tw / 2 + window.scrollX;
        break;
      case "left":
        top = rect.top + rect.height / 2 - th / 2 + window.scrollY;
        left = rect.left - tw - GAP + window.scrollX;
        break;
      case "right":
        top = rect.top + rect.height / 2 - th / 2 + window.scrollY;
        left = rect.right + GAP + window.scrollX;
        break;
      default:
        top = rect.bottom + GAP + window.scrollY;
        left = rect.left + rect.width / 2 - tw / 2 + window.scrollX;
    }

    setCoords({ top, left });
  };

  const handleMouseEnter = () => {
    if (disabled || !content) return;
    showTimeout.current = setTimeout(() => {
      setVisible(true);
      // Re-calcular después de que el elemento se renderice
      requestAnimationFrame(compute);
    }, 120);
  };

  const handleMouseLeave = () => {
    clearTimeout(showTimeout.current);
    setVisible(false);
  };

  useEffect(() => {
    if (visible) {
      compute();
    }
  }, [visible]);

  useEffect(() => {
    return () => clearTimeout(showTimeout.current);
  }, []);

  const arrowStyles = {
    top: {
      bottom: "-5px",
      left: "50%",
      transform: "translateX(-50%)",
      borderLeft: "5px solid transparent",
      borderRight: "5px solid transparent",
      borderTop: "5px solid #1a4a50",
    },
    bottom: {
      top: "-5px",
      left: "50%",
      transform: "translateX(-50%)",
      borderLeft: "5px solid transparent",
      borderRight: "5px solid transparent",
      borderBottom: "5px solid #1a4a50",
    },
    left: {
      right: "-5px",
      top: "50%",
      transform: "translateY(-50%)",
      borderTop: "5px solid transparent",
      borderBottom: "5px solid transparent",
      borderLeft: "5px solid #1a4a50",
    },
    right: {
      left: "-5px",
      top: "50%",
      transform: "translateY(-50%)",
      borderTop: "5px solid transparent",
      borderBottom: "5px solid transparent",
      borderRight: "5px solid #1a4a50",
    },
  };

  const tooltipEl = (
    <div
      ref={tooltipRef}
      style={{
        position: "absolute",
        top: coords.top,
        left: coords.left,
        zIndex: 99999,
        pointerEvents: "none",
        opacity: visible ? 1 : 0,
        transform: visible ? "scale(1) translateY(0)" : "scale(0.88) translateY(-4px)",
        transition: "opacity 0.18s ease, transform 0.18s cubic-bezier(0.34,1.56,0.64,1)",
      }}
    >
      {/* Contenedor principal */}
      <div
        style={{
          background: "linear-gradient(135deg, #1a4a50 0%, #216b6b 60%, #246370 100%)",
          color: "#fff",
          padding: "6px 14px",
          borderRadius: "20px",
          fontSize: "12.5px",
          fontWeight: "500",
          letterSpacing: "0.015em",
          whiteSpace: "nowrap",
          boxShadow:
            "0 8px 24px rgba(33,107,107,0.35), 0 2px 8px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.15)",
          border: "1px solid rgba(255,255,255,0.12)",
          backdropFilter: "blur(8px)",
          position: "relative",
        }}
      >
        {content}
        {/* Flecha */}
        <span
          style={{
            position: "absolute",
            width: 0,
            height: 0,
            ...arrowStyles[position],
          }}
        />
      </div>
    </div>
  );

  return (
    <span
      ref={triggerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{ display: "inline-flex" }}
    >
      {children}
      {typeof document !== "undefined" &&
        ReactDOM.createPortal(tooltipEl, document.body)}
    </span>
  );
}